import { useEffect, useRef, useCallback } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

const norm = (s) => String(s || "").trim().toUpperCase();

// Escala: Azul cielo → Índigo → Púrpura → Rosa
const COLOR_SCALE = [
  { t: 0,    color: [226, 232, 240] }, // slate-200   (sin mercados)
  { t: 0.12, color: [186, 230, 253] }, // sky-200
  { t: 0.30, color: [165, 180, 252] }, // indigo-200
  { t: 0.50, color: [129, 140, 248] }, // indigo-400
  { t: 0.70, color: [192, 132, 252] }, // purple-400
  { t: 0.85, color: [236,  72, 153] }, // pink-500
  { t: 1.0,  color: [190,  24,  93] }, // pink-800
];

function interpolateColor(t) {
  if (t <= 0) return COLOR_SCALE[0].color;
  if (t >= 1) return COLOR_SCALE[COLOR_SCALE.length - 1].color;
  let lo = COLOR_SCALE[0], hi = COLOR_SCALE[1];
  for (let i = 1; i < COLOR_SCALE.length; i++) {
    if (t <= COLOR_SCALE[i].t) { lo = COLOR_SCALE[i - 1]; hi = COLOR_SCALE[i]; break; }
  }
  const f = (t - lo.t) / (hi.t - lo.t);
  return lo.color.map((c, i) => Math.round(c + f * (hi.color[i] - c)));
}

function getStyle(count, max) {
  const t = max > 0 ? count / max : 0;
  const [r, g, b] = interpolateColor(t);
  return {
    fillColor: `rgb(${r},${g},${b})`,
    fillOpacity: count === 0 ? 0.12 : 0.15,
    color: "rgba(71,85,105,0.5)",
    weight: 1,
    dashArray: count === 0 ? "4" : "",
  };
}

function getHiddenStyle() {
  return { fillOpacity: 0, opacity: 0 };
}

// Genera el CSS gradient string para la barra horizontal
function buildGradientCSS() {
  const stops = COLOR_SCALE.map(({ t, color }) => {
    const [r, g, b] = color;
    return `rgb(${r},${g},${b}) ${Math.round(t * 100)}%`;
  });
  return `linear-gradient(to right, ${stops.join(", ")})`;
}

function createLegend(map, { max }, onToggle, isVisible) {
  const legend = L.control({ position: "bottomleft" });
  legend.onAdd = () => {
    const div = L.DomUtil.create("div", "choropleth-legend");
    const gradientCSS = buildGradientCSS();
    const checkedAttr = isVisible ? "checked" : "";
    div.innerHTML = `
      <label class="choropleth-legend-header">
        <input type="checkbox" class="choropleth-chk" ${checkedAttr} />
        <span>Mercados por departamento</span>
      </label>
      <div class="choropleth-legend-scale">
        <div class="choropleth-legend-bar" style="background:${gradientCSS}"></div>
        <div class="choropleth-legend-ticks">
          <span>0</span>
          <span>${Math.round(max * 0.25)}</span>
          <span>${Math.round(max * 0.5)}</span>
          <span>${Math.round(max * 0.75)}</span>
          <span>${max}</span>
        </div>
      </div>
    `;
    L.DomEvent.disableClickPropagation(div);
    div.querySelector(".choropleth-chk").addEventListener("change", (e) => {
      onToggle(e.target.checked);
    });
    return div;
  };
  legend.addTo(map);
  return legend;
}

const ChoroplethDptoLayer = ({ points }) => {
  const map = useMap();
  const layerRef = useRef(null);
  const legendRef = useRef(null);
  const countsRef = useRef({});
  const maxRef = useRef(1);
  const visibleRef = useRef(false);

  const rebuildLegend = useCallback((max) => {
    if (legendRef.current) { map.removeControl(legendRef.current); legendRef.current = null; }
    legendRef.current = createLegend(map, { max }, (checked) => {
      visibleRef.current = checked;
      if (!layerRef.current) return;
      if (checked) {
        layerRef.current.eachLayer((l) => {
          const props = l.feature?.properties;
          if (!props) return;
          const count = countsRef.current[norm(props.departamento)] ?? 0;
          l.setStyle(getStyle(count, maxRef.current));
        });
      } else {
        layerRef.current.setStyle(getHiddenStyle());
      }
    }, visibleRef.current);
  }, [map]);

  // Cargar GeoJSON una sola vez
  useEffect(() => {
    let cancelled = false;

    const loadGeoJSON = async () => {
      const mod = await import("../data/geojson/dpto.json");
      const urlOrData = mod.default ?? mod;
      if (typeof urlOrData === "string") {
        const res = await fetch(urlOrData);
        return res.json();
      }
      return urlOrData;
    };

    loadGeoJSON()
      .then((geoData) => {
        if (cancelled || layerRef.current) return;
        if (!geoData?.features) { console.error("[Choropleth] GeoJSON inválido", geoData); return; }

        layerRef.current = L.geoJSON(geoData, {
          style: (feature) => {
            const depName = norm(feature.properties?.departamento);
            const count = countsRef.current[depName] ?? 0;
            return getStyle(count, maxRef.current);
          },
          onEachFeature: (feature, layer) => {
            layer.on({
              mouseover(e) {
                if (!visibleRef.current) return;
                e.target.setStyle({ weight: 2.5, color: "rgba(15,23,42,0.8)" });
                e.target.bringToFront();
              },
              mouseout(e) {
                if (layerRef.current) layerRef.current.resetStyle(e.target);
                if (!visibleRef.current) e.target.setStyle(getHiddenStyle());
              },
            });
          },
        });

        layerRef.current.addTo(map);
        layerRef.current.bringToBack();
        layerRef.current.setStyle(getHiddenStyle());
        rebuildLegend(maxRef.current);
      })
      .catch((err) => console.error("[Choropleth] Error:", err));

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Actualizar colores cuando cambian los puntos
  useEffect(() => {
    const counts = {};
    (points || []).forEach((item) => {
      const dep = norm(item.departamento);
      if (dep) counts[dep] = (counts[dep] || 0) + 1;
    });

    const max = Math.max(1, ...Object.values(counts));
    countsRef.current = counts;
    maxRef.current = max;

    if (layerRef.current) {
      layerRef.current.eachLayer((layer) => {
        const props = layer.feature?.properties;
        if (!props) return;
        const depName = norm(props.departamento);
        const count = counts[depName] ?? 0;

        if (visibleRef.current) {
          layer.setStyle(getStyle(count, max));
        }

        const label = count === 1 ? "1 mercado" : `${count} mercados`;
        layer.unbindTooltip();
        layer.bindTooltip(
          `<div class="choropleth-tip"><b>${props.departamento}</b><span>${label}</span></div>`,
          { sticky: true, opacity: 1 }
        );
      });
    }

    rebuildLegend(max);
  }, [points, map, rebuildLegend]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
      if (legendRef.current) { map.removeControl(legendRef.current); legendRef.current = null; }
    };
  }, [map]);

  return null;
};

export default ChoroplethDptoLayer;
